import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { partnerGroupChannelQueryKeys } from '../api/use-partner-group-channel-query';
import {
  buildPartnerGroupChannelMessageMapOptions,
  hasMeaningfulReactionFields,
} from '../components/Partner/Community/mapPartnerGroupChannel';
import {
  applyPartnerGroupChatOptimisticReactionInCache,
  buildPartnerGroupChatSocketReactionCachePatch,
  resolveReactorUserFromChannelCache,
  updatePartnerGroupChatMessageReactionsInCache,
} from '../components/Partner/Community/partnerGroupChannelMessagesCache';
import {
  doesSocketChannelMatchLiveCommunityView,
  getPartnerCommunityLiveView,
} from '../components/Partner/Community/partnerCommunityLiveView';
import {
  applyRemoteReactionAdded,
  applyRemoteReactionRemoved,
  isSupportedReactionType,
} from '../components/Partner/Community/communityChatReactions';
import { SOCKET_EVENTS } from '../constants/socketEvents';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketProvider';
import { useSocketEvent } from './useSocketEvent';
import { hydrateMediaUrls } from '../utils/mediaSas';
import {
  consumeSocketIdempotencyKey,
  collectPartnerGroupChatReactionActorIds,
  getPartnerGroupChatSocketChannelId,
  getPartnerGroupChatSocketMessageId,
  getSocketEnvelopeBody,
  mapPartnerGroupChatSocketToReactionUpdate,
  shouldSkipOwnReactionSocketEvent,
} from '../utils/partnerGroupChatSocketPayload';

/** True when the envelope carries Stream reaction state we can patch from (not own_reactions alone). */
const hasStreamReactionState = (body = {}) =>
  body.reaction_counts != null || body.reaction_groups != null || body.latest_reactions != null;

/**
 * Real-time message:reaction_added / message:reaction_removed for partner + admin chat UI.
 * Patches the live channel cache like message:new (skip own fanout — optimistic UI already applied).
 */
export const useMessageReactionSocketListener = ({ enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const { isAdmin, isPartner, firebaseUid, partnerProfile, adminProfile } = useAuth();
  const { isConnected } = useSocket();
  const canListen = Boolean(isAdmin || isPartner);

  const messageMapOptions = useMemo(
    () =>
      buildPartnerGroupChannelMessageMapOptions({
        firebaseUid,
        partnerProfile: isAdmin ? adminProfile : partnerProfile,
      }),
    [adminProfile, firebaseUid, isAdmin, partnerProfile],
  );

  const invalidateMessages = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'],
    });
  }, [queryClient]);

  const handleReactionEvent = useCallback(
    (envelope, action) => {
      if (!canListen) return;
      if (consumeSocketIdempotencyKey(envelope?.idempotencyKey)) return;

      hydrateMediaUrls(envelope);

      if (
        shouldSkipOwnReactionSocketEvent(envelope, firebaseUid, messageMapOptions.currentUserIds)
      ) {
        return;
      }

      const body = getSocketEnvelopeBody(envelope);
      const socketChannelId = getPartnerGroupChatSocketChannelId(envelope);
      const messageId = getPartnerGroupChatSocketMessageId(envelope);
      const reactionType = String(body?.reactionType ?? body?.type ?? '')
        .trim()
        .toLowerCase();

      if (!messageId || !socketChannelId) {
        return;
      }

      const liveView = getPartnerCommunityLiveView();
      const isViewingSameChannel =
        doesSocketChannelMatchLiveCommunityView(socketChannelId) && Boolean(liveView.active);

      if (!isViewingSameChannel) {
        invalidateMessages();
        return;
      }

      if (hasStreamReactionState(body)) {
        const reactionUpdate = mapPartnerGroupChatSocketToReactionUpdate(
          envelope,
          messageMapOptions,
        );
        if (reactionUpdate?.messageId && hasMeaningfulReactionFields(reactionUpdate)) {
          const patch = buildPartnerGroupChatSocketReactionCachePatch(queryClient, reactionUpdate, {
            channelId: socketChannelId,
            messageMapOptions,
          });
          if (patch) {
            updatePartnerGroupChatMessageReactionsInCache(
              queryClient,
              reactionUpdate.messageId,
              patch,
            );
            return;
          }
        }
      }

      if (!isSupportedReactionType(reactionType)) {
        invalidateMessages();
        return;
      }

      const actorIds = collectPartnerGroupChatReactionActorIds(envelope);
      const currentIds = messageMapOptions?.currentUserIds;
      const isOwn = actorIds.some(
        (id) =>
          (currentIds instanceof Set && currentIds.has(id)) ||
          (firebaseUid && id === String(firebaseUid).trim()),
      );

      const reactorUser = resolveReactorUserFromChannelCache(queryClient, {
        actorIds,
        channelId: socketChannelId,
        isOwn,
        senderDefaults: messageMapOptions?.senderDefaults,
      });

      const applyRemote =
        action === 'removed' ? applyRemoteReactionRemoved : applyRemoteReactionAdded;

      const applied = applyPartnerGroupChatOptimisticReactionInCache(
        queryClient,
        messageId,
        reactionType,
        (message, type) => applyRemote(message, type, { isOwn, user: reactorUser }),
      );

      if (!applied) {
        invalidateMessages();
      }
    },
    [canListen, firebaseUid, invalidateMessages, messageMapOptions, queryClient],
  );

  const handleReactionAdded = useCallback(
    (envelope) => handleReactionEvent(envelope, 'added'),
    [handleReactionEvent],
  );

  const handleReactionRemoved = useCallback(
    (envelope) => handleReactionEvent(envelope, 'removed'),
    [handleReactionEvent],
  );

  useSocketEvent(
    SOCKET_EVENTS.MESSAGE_REACTION_ADDED,
    enabled && canListen && isConnected ? handleReactionAdded : null,
    [enabled, canListen, isConnected, handleReactionAdded],
  );

  useSocketEvent(
    SOCKET_EVENTS.MESSAGE_REACTION_REMOVED,
    enabled && canListen && isConnected ? handleReactionRemoved : null,
    [enabled, canListen, isConnected, handleReactionRemoved],
  );
};

export default useMessageReactionSocketListener;
